import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Modal, Form, Button } from 'react-bootstrap';
import AppContext from 'context/Context';
import { patch } from 'services/bs-sector-measurable/targetService';
import {
  compareTypeSymbol,
  isBlankValue,
  RAG_RATING_OPTIONS,
  SIC_VALUE_KIND,
  storedMinutesToTimeText,
  timeTextToStoredMinutes
} from 'helpers/sicHelper';
import {
  swalToastError,
  swalToastSuccess
} from 'components/utilities/SwalToastUtil';

const targetToInputText = (target, valueKind) => {
  if (isBlankValue(target)) return '';
  return valueKind === SIC_VALUE_KIND.time
    ? storedMinutesToTimeText(target)
    : String(target);
};

const inputTextToTarget = (inputText, valueKind) =>
  valueKind === SIC_VALUE_KIND.time
    ? String(timeTextToStoredMinutes(inputText))
    : inputText;

const TARGET_INPUT_PROPS_BY_VALUE_KIND = {
  [SIC_VALUE_KIND.date]: { type: 'number', min: 0, step: 1 },
  [SIC_VALUE_KIND.time]: { type: 'time' }
};

const DEFAULT_TARGET_INPUT_PROPS = { type: 'number', step: 'any' };

const TARGET_DESCRIPTION_BY_VALUE_KIND = {
  [SIC_VALUE_KIND.date]: compareSymbol =>
    `Targets are set per month as working days allowed (Mon-Fri). Achieved when working days from the row date to the entered date ${compareSymbol} target.`,
  [SIC_VALUE_KIND.time]: compareSymbol =>
    `Targets are set per month as a time (hh:mm). Achieved when the entered time ${compareSymbol} target.`,
  [SIC_VALUE_KIND.rag]: () =>
    'Targets are set per month as the lowest acceptable rating. Green target: only Green is achieved. Amber target: Green and Amber are achieved.'
};

const defaultTargetDescription = compareSymbol =>
  `Targets are set per month. Achieved when value ${compareSymbol} target.`;

const buildTargetByMonthStart = (measurable, weekDates, monthStarts) =>
  Object.fromEntries(
    monthStarts.map(monthStart => {
      const firstDayIndexInMonth = weekDates.findIndex(
        date => date.startOf('month').format('YYYY-MM-DD') === monthStart
      );
      const target = measurable.targets[firstDayIndexInMonth];
      return [monthStart, targetToInputText(target, measurable.valueKind)];
    })
  );

export default function SicTargetModal({
  measurable,
  weekDates,
  monthStarts,
  handleClose,
  handleSaved
}) {
  const {
    config: { isDark }
  } = useContext(AppContext);
  const [initialTargetByMonthStart] = useState(() =>
    buildTargetByMonthStart(measurable, weekDates, monthStarts)
  );
  const [targetByMonthStart, setTargetByMonthStart] = useState(
    initialTargetByMonthStart
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async event => {
    event.preventDefault();
    const changedMonthStarts = monthStarts.filter(
      monthStart =>
        targetByMonthStart[monthStart] !== '' &&
        targetByMonthStart[monthStart] !==
          initialTargetByMonthStart[monthStart]
    );
    if (!changedMonthStarts.length) {
      handleClose();
      return;
    }

    setIsSaving(true);
    const responses = await Promise.all(
      changedMonthStarts.map(monthStart =>
        patch(measurable.id, {
          target: inputTextToTarget(
            targetByMonthStart[monthStart],
            measurable.valueKind
          ),
          date: monthStart
        })
      )
    );
    setIsSaving(false);

    if (responses.some(response => !response?.success)) {
      swalToastError('Failed to save target.');
      return;
    }
    swalToastSuccess('Target saved');
    handleSaved();
  };

  return (
    <Modal show onHide={handleClose} contentClassName="border">
      <Form onSubmit={handleSubmit}>
        <Modal.Header
          closeButton
          closeVariant={isDark ? 'white' : undefined}
          className="bg-light px-card border-bottom-0"
        >
          <Modal.Title as="h5">Target - {measurable.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-card">
          <div className="fs--1 text-muted mb-3">
            {(
              TARGET_DESCRIPTION_BY_VALUE_KIND[measurable.valueKind] ??
              defaultTargetDescription
            )(compareTypeSymbol(measurable.compareType))}
          </div>
          {monthStarts.map(monthStart => {
            const updateMonthTarget = e =>
              setTargetByMonthStart(previous => ({
                ...previous,
                [monthStart]: e.target.value
              }));
            return (
              <Form.Group key={monthStart} className="mb-3">
                <Form.Label className="fs-0">
                  {dayjs(monthStart).format('MMMM YYYY')}
                </Form.Label>
                {measurable.valueKind === SIC_VALUE_KIND.rag ? (
                  <Form.Select
                    value={targetByMonthStart[monthStart]}
                    onChange={updateMonthTarget}
                  >
                    <option value="">Select rating</option>
                    {RAG_RATING_OPTIONS.map(option => (
                      <option key={option.value} value={String(option.value)}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    {...(TARGET_INPUT_PROPS_BY_VALUE_KIND[
                      measurable.valueKind
                    ] ?? DEFAULT_TARGET_INPUT_PROPS)}
                    value={targetByMonthStart[monthStart]}
                    onChange={updateMonthTarget}
                  />
                )}
              </Form.Group>
            );
          })}
        </Modal.Body>
        <Modal.Footer className="bg-light px-card border-top-0">
          <Button
            variant="primary"
            type="submit"
            className="px-4 mx-0"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

SicTargetModal.propTypes = {
  measurable: PropTypes.object.isRequired,
  weekDates: PropTypes.array.isRequired,
  monthStarts: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSaved: PropTypes.func.isRequired
};
